import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ProdutoList.css';
import api from '../services/api';
import { toast } from 'react-toastify';

function ProdutoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const fileInputRef = useRef(null);

  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [preco, setPreco] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [estoqueMinimo, setEstoqueMinimo] = useState('5');
  const [imagem, setImagem] = useState(null);
  const [imagemPreview, setImagemPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [produtos, setProdutos] = useState([]);

  useEffect(() => {
    fetchRecentes();
    if (isEditing) fetchProduto();
  }, [id]);

  const fetchProduto = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await api.get(`/produtos/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { nome, descricao, preco, quantidade_estoque, estoque_minimo, imagem } = res.data;
      setNome(nome);
      setDescricao(descricao || '');
      setPreco(preco);
      setQuantidade(quantidade_estoque);
      setEstoqueMinimo(String(estoque_minimo ?? 5));
      if (imagem) setImagemPreview(imagem);
    } catch {
      toast.error('Erro ao carregar o produto.');
    }
  };

  const fetchRecentes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await api.get('/produtos/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProdutos(res.data.slice(0, 5));
    } catch {}
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImagem(file);
    setImagemPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };

    const formData = new FormData();
    formData.append('nome', nome);
    formData.append('descricao', descricao);
    formData.append('preco', parseFloat(preco));
    formData.append('quantidade_estoque', parseInt(quantidade, 10));
    formData.append('estoque_minimo', parseInt(estoqueMinimo, 10) || 0);
    if (imagem) formData.append('imagem', imagem);

    try {
      if (isEditing) {
        await api.patch(`/produtos/${id}/`, formData, { headers });
        toast.success('Produto atualizado com sucesso!');
      } else {
        await api.post('/produtos/', formData, { headers });
        toast.success('Produto cadastrado com sucesso!');
      }
      navigate('/produtos');
    } catch (err) {
      if (err._planLimitHandled) return;
      toast.error(isEditing ? 'Erro ao atualizar o produto.' : 'Erro ao cadastrar o produto.');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  const getStatus = (qty, minimo = 5) => {
    if (qty === 0)    return { label: 'Sem estoque', cls: 'badge-error' };
    if (qty < minimo) return { label: 'Crítico',     cls: 'badge-warn' };
    return                   { label: 'Em estoque',  cls: 'badge-ok' };
  };

  return (
    <div className="produto-page">

      {/* Page sub-header */}
      <div className="produto-page-header">
        <div className="produto-page-title-row">
          <h2 className="produto-page-title">Produtos</h2>
          <div className="produto-tabs">
            <span className="produto-tab active">
              {isEditing ? 'Editar Produto' : 'Novo Produto'}
            </span>
            <span className="produto-tab" onClick={() => navigate('/produtos')}>
              Lista de Produtos
            </span>
          </div>
        </div>
      </div>

      {/* Form card */}
      <div className="prd-card prd-form-card">
        <div className="prd-card-accent" />
        <div className="prd-form-body">

          <header className="prd-form-header">
            <h3 className="prd-form-title">Informações do Produto</h3>
            <p className="prd-form-sub">
              {isEditing
                ? 'Atualize os dados e métricas de estoque do item.'
                : 'Preencha os dados para cadastrar um novo produto no inventário.'}
            </p>
          </header>

          <form onSubmit={handleSubmit} className="prd-form-grid">

            {/* Left column */}
            <div className="prd-form-left">

              <div className="prd-field prd-field--full">
                <label className="prd-label">Nome do Produto</label>
                <input
                  className="prd-input"
                  type="text"
                  placeholder="Ex: Vidro Temperado 8mm"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                />
              </div>

              <div className="prd-field prd-field--full">
                <label className="prd-label">Descrição</label>
                <textarea
                  className="prd-input prd-textarea"
                  rows={4}
                  placeholder="Descreva o produto, especificações técnicas..."
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                />
              </div>

              <div className="prd-form-row3" style={{ gridTemplateColumns: '1fr 130px 130px 1fr' }}>
                <div className="prd-field">
                  <label className="prd-label">Preço (R$)</label>
                  <div className="prd-input-prefix-wrap">
                    <span className="prd-input-prefix">R$</span>
                    <input
                      className="prd-input prd-input--prefixed"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0,00"
                      value={preco}
                      onChange={(e) => setPreco(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="prd-field">
                  <label className="prd-label">Estoque Atual</label>
                  <input
                    className="prd-input"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={quantidade}
                    onChange={(e) => setQuantidade(e.target.value)}
                    required
                  />
                </div>

                <div className="prd-field">
                  <label className="prd-label">Estoque Mínimo</label>
                  <input
                    className="prd-input"
                    type="number"
                    min="0"
                    placeholder="5"
                    value={estoqueMinimo}
                    onChange={(e) => setEstoqueMinimo(e.target.value)}
                    title="Alerta será acionado quando o estoque cair abaixo deste valor"
                  />
                </div>

                <div className="prd-field">
                  <label className="prd-label">Valor em Estoque</label>
                  <input
                    className="prd-input prd-input--readonly"
                    type="text"
                    readOnly
                    value={
                      preco && quantidade
                        ? formatCurrency(parseFloat(preco) * parseInt(quantidade || 0))
                        : '—'
                    }
                  />
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="prd-form-right">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <div
                className="prd-upload-area"
                onClick={() => fileInputRef.current.click()}
                style={{ cursor: 'pointer', overflow: 'hidden' }}
              >
                {imagemPreview ? (
                  <img
                    src={imagemPreview}
                    alt="Preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                  />
                ) : (
                  <>
                    <span className="material-symbols-outlined prd-upload-icon">upload_file</span>
                    <p className="prd-upload-label">Clique para enviar imagem</p>
                    <p className="prd-upload-sub">PNG, JPG, WEBP (máx. 5MB)</p>
                  </>
                )}
              </div>

              <div className="prd-forecast-card">
                <div className="prd-forecast-title">
                  <span className="material-symbols-outlined">info</span>
                  Resumo do Estoque
                </div>
                <div className="prd-forecast-row">
                  <span>Qtd. atual</span>
                  <span className="prd-forecast-val">{quantidade || '—'}</span>
                </div>
                <div className="prd-forecast-row">
                  <span>Valor unitário</span>
                  <span className="prd-forecast-val">
                    {preco ? formatCurrency(parseFloat(preco)) : '—'}
                  </span>
                </div>
                <div className="prd-forecast-row">
                  <span>Valor total</span>
                  <span className="prd-forecast-val prd-forecast-highlight">
                    {preco && quantidade
                      ? formatCurrency(parseFloat(preco) * parseInt(quantidade || 0))
                      : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="prd-form-actions">
              <button
                type="button"
                className="prd-btn-cancel"
                onClick={() => navigate('/produtos')}
              >
                Cancelar
              </button>
              <button type="submit" className="prd-btn-save" disabled={saving}>
                <span className="material-symbols-outlined">save</span>
                {saving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Cadastrar Produto'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Recent entries table */}
      <div className="prd-recentes">
        <div className="prd-recentes-header">
          <h4 className="prd-recentes-title">Entradas Recentes</h4>
          <button className="prd-btn-link" onClick={() => navigate('/produtos')}>
            Ver todos
          </button>
        </div>
        <div className="prd-card prd-table-card" style={{ marginTop: 0 }}>
          <div className="prd-table-wrap">
            <table className="prd-table">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Status do Estoque</th>
                  <th className="right">Preço</th>
                  <th className="right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {produtos.length === 0 ? (
                  <tr><td colSpan={4} className="prd-empty">Nenhum produto.</td></tr>
                ) : (
                  produtos.map((p) => {
                    const st = getStatus(p.quantidade_estoque, p.estoque_minimo ?? 5);
                    return (
                      <tr key={p.id}>
                        <td className="td-name">
                          <div className="td-name-wrap">
                            <div className="td-thumb">
                              {p.imagem && (
                                <img src={p.imagem} alt={p.nome} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} />
                              )}
                            </div>
                            <span>{p.nome}</span>
                          </div>
                        </td>
                        <td><span className={`badge ${st.cls}`}>{st.label}</span></td>
                        <td className="right td-price">{formatCurrency(p.preco)}</td>
                        <td className="right">
                          <button
                            className="prd-icon-btn"
                            onClick={() => navigate(`/produtos/editar/${p.id}`)}
                          >
                            <span className="material-symbols-outlined">edit</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProdutoForm;
